import { useMemo } from 'react';
import type { BackupEntry, BackupStatus } from '@/mocks/saGlobalBackupCenter';
import { storageInfo, statusMeta } from '@/mocks/saGlobalBackupCenter';

interface BackupStatsBarProps {
  entries: BackupEntry[];
}

export default function BackupStatsBar({ entries }: BackupStatsBarProps) {
  const counts = useMemo(() => {
    const c: Record<BackupStatus | 'total', number> = { total: entries.length, success: 0, in_progress: 0, failed: 0, pending: 0 };
    entries.forEach((e) => { c[e.status] = (c[e.status] || 0) + 1; });
    return c;
  }, [entries]);

  const totalSize = useMemo(() => entries.reduce((sum, e) => sum + e.size, 0), [entries]);

  const stats = [
    { label: 'Total Backups', value: counts.total, icon: 'ri-archive-line', color: 'text-foreground-700', bg: 'bg-background-200' },
    { label: statusMeta.success.label, value: counts.success, icon: statusMeta.success.icon, color: statusMeta.success.color, bg: statusMeta.success.bg },
    { label: statusMeta.in_progress.label, value: counts.in_progress, icon: statusMeta.in_progress.icon, color: statusMeta.in_progress.color, bg: statusMeta.in_progress.bg },
    { label: statusMeta.failed.label, value: counts.failed, icon: statusMeta.failed.icon, color: statusMeta.failed.color, bg: statusMeta.failed.bg },
    { label: statusMeta.pending.label, value: counts.pending, icon: statusMeta.pending.icon, color: statusMeta.pending.color, bg: statusMeta.pending.bg },
    { label: 'Tamaño Total', value: `${totalSize.toFixed(1)} GB`, icon: 'ri-hard-drive-2-line', color: 'text-foreground-700', bg: 'bg-background-200' },
  ];

  const usageColor = storageInfo.usagePercent > 90 ? 'bg-red-500' : storageInfo.usagePercent > 75 ? 'bg-amber-500' : 'bg-emerald-500';
  const usageTextColor = storageInfo.usagePercent > 90 ? 'text-red-600' : storageInfo.usagePercent > 75 ? 'text-amber-600' : 'text-emerald-600';

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {stats.map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center shrink-0`}>
              <i className={`${s.icon} ${s.color} text-lg`}></i>
            </div>
            <div className="min-w-0">
              <p className="text-foreground-400 text-[10px] font-medium leading-tight">{s.label}</p>
              <p className="text-foreground-900 text-sm font-bold">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Storage bar */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <i className="ri-database-2-line text-foreground-500 text-lg"></i>
            <div>
              <p className="text-foreground-800 text-xs font-semibold">Espacio de Almacenamiento</p>
              <p className="text-foreground-400 text-[10px]">
                {storageInfo.usedGB.toFixed(1)} GB usado de {storageInfo.totalGB} GB · {storageInfo.availableGB.toFixed(1)} GB disponible
              </p>
            </div>
          </div>
          <span className={`text-xs font-bold ${usageTextColor} whitespace-nowrap`}>
            {storageInfo.usagePercent.toFixed(1)}% utilizado
          </span>
        </div>
        <div className="w-full h-3 rounded-full bg-background-200 overflow-hidden">
          <div
            className={`h-full rounded-full ${usageColor} transition-all duration-500`}
            style={{ width: `${Math.min(storageInfo.usagePercent, 100)}%` }}
          ></div>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-foreground-400 text-[10px]">0 GB</span>
          <span className="text-foreground-400 text-[10px]">{storageInfo.totalGB} GB</span>
        </div>
      </div>
    </div>
  );
}